import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { CalendarDays, RefreshCcw, PlusCircle, Settings2, LogIn } from "lucide-react";
import { toast } from "sonner";

const DAY_COUNT = 31;

const pad2 = (n) => String(n).padStart(2, "0");

const parseRedDates = (raw) => new Set(
  String(raw || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean)
    .map((x) => Number(x))
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 31),
);

const monthLabel = (month) => {
  try {
    const [y, m] = month.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  } catch {
    return month;
  }
};

const daysInMonth = (month) => {
  try {
    const [y, m] = month.split("-").map(Number);
    return new Date(y, m, 0).getDate();
  } catch {
    return 31;
  }
};

const dayHeaderClass = (month, day, redDatesSet) => {
  if (redDatesSet.has(day)) return "!bg-rose-100 !text-rose-700";
  try {
    const [year, mm] = month.split("-").map(Number);
    if (day > daysInMonth(month)) return "";
    const dow = new Date(year, mm - 1, day).getDay();
    if (dow === 0) return "!bg-rose-100 !text-rose-700";
    if (dow === 6) return "!bg-emerald-200 !text-emerald-700";
  } catch {
    return "";
  }
  return "";
};

const jadwalCellClass = (value) => {
  const v = String(value || "").trim().toUpperCase();
  if (v === "H") return "!bg-rose-100 !text-rose-700";
  if (v === "1") return "!bg-white !text-slate-700";
  if (v === "2") return "!bg-white !text-slate-700";
  if (v === "3") return "!bg-white !text-slate-700";
  return "";
};

function monthInputToNext(month) {
  const [year, mm] = String(month).split("-").map(Number);
  if (!year || !mm) return month;
  const nextYear = mm === 12 ? year + 1 : year;
  const nextMonth = mm === 12 ? 1 : mm + 1;
  return `${nextYear}-${pad2(nextMonth)}`;
}

export default function WtrSi() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState("");
  const [redDates, setRedDates] = useState("");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [savingKey, setSavingKey] = useState("");
  const [showConfig, setShowConfig] = useState(false);
  const [redDatesInput, setRedDatesInput] = useState("");
  const [creatingMonth, setCreatingMonth] = useState(false);

  const redDatesSet = useMemo(() => parseRedDates(redDates), [redDates]);

  const load = useCallback(async (targetMonth = "") => {
    setLoading(true);
    try {
      const params = {};
      if (targetMonth) params.month = targetMonth;
      const { data } = await api.get("/wtr-si", { params });
      setRows(data.rows || []);
      setMonths(data.months || []);
      setMonth(data.month || targetMonth || "");
      setRedDates(data.red_dates || "");
    } catch {
      toast.error("Gagal memuat data WTR SI");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const groupedTeams = useMemo(() => {
    const orderedRows = [...rows].sort((a, b) => Number(a.__row || 0) - Number(b.__row || 0));
    const map = {};
    const order = [];
    for (const row of orderedRows) {
      const team = row.team || "-";
      if (!map[team]) {
        map[team] = [];
        order.push(team);
      }
      map[team].push(row);
    }
    return order.map((team) => {
      const allRows = map[team];
      const jadwalRows = allRows.filter((r) => r.type === "jadwal");
      const personelRows = allRows.filter((r) => r.type !== "jadwal");
      return { team, rows: [...jadwalRows, ...personelRows] };
    });
  }, [rows]);

  const beginEdit = (row, day) => {
    if (!user) {
      toast.error("Login dulu untuk edit");
      return;
    }
    const key = `${row.__row}-${day}`;
    setEditing(key);
    setDraft(String(row[`d${day}`] || ""));
  };

  const finishEdit = async (row, day, shouldSave) => {
    const key = `${row.__row}-${day}`;
    if (editing !== key) return;
    const newVal = String(draft || "");
    setEditing(null);
    setDraft("");
    if (!shouldSave) return;
    const oldVal = String(row[`d${day}`] || "");
    if (newVal === oldVal) return;

    setSavingKey(key);
    try {
      await api.put("/wtr-si/cell", {
        row_number: Number(row.__row),
        day: `d${day}`,
        value: newVal,
      });
      setRows((prev) => prev.map((r) => (r.__row === row.__row ? { ...r, [`d${day}`]: newVal } : r)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan sel");
    } finally {
      setSavingKey("");
    }
  };

  const handleCreateNextMonth = async () => {
    if (!user) {
      toast.error("Login dulu untuk membuat bulan baru");
      return;
    }
    setCreatingMonth(true);
    try {
      const { data } = await api.post("/wtr-si/next-month", { current_month: month });
      const nextMonth = data?.result?.month || monthInputToNext(month);
      if (data?.result?.created) {
        toast.success(`Bulan ${nextMonth} berhasil dibuat`);
      } else {
        toast.success(`Bulan ${nextMonth} sudah ada`);
      }
      await load(nextMonth);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal membuat bulan baru");
    } finally {
      setCreatingMonth(false);
    }
  };

  const openRedDatesDialog = () => {
    setRedDatesInput(redDates || "");
    setShowConfig(true);
  };

  const saveRedDates = async () => {
    if (!user) {
      toast.error("Login dulu untuk mengatur tanggal merah");
      return;
    }
    const clean = String(redDatesInput || "").replace(/\s+/g, "");
    try {
      await api.put("/wtr-si/red-dates", { month, red_dates: clean });
      setRedDates(clean);
      setShowConfig(false);
      toast.success("Tanggal merah disimpan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan tanggal merah");
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1800px] mx-auto">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-5">
        <div>
          <div className="label-ind">Shift Planner</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">WTR SI</h1>
          <p className="text-sm text-slate-500 mt-1">Jadwal shift bulanan terhubung langsung ke Google Sheets.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!user && (
            <Button variant="outline" onClick={() => nav("/login")} className="rounded-sm">
              <LogIn className="w-4 h-4 mr-1" /> Login untuk Edit
            </Button>
          )}
          <Button variant="outline" onClick={() => load(month)} className="rounded-sm" data-testid="btn-wtr-refresh">
            <RefreshCcw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button onClick={handleCreateNextMonth} disabled={creatingMonth || !month} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" data-testid="btn-wtr-next-month">
            <PlusCircle className="w-4 h-4 mr-1" /> {creatingMonth ? "Membuat..." : "Bulan Baru"}
          </Button>
          <Button variant="outline" onClick={openRedDatesDialog} disabled={!month} className="rounded-sm" data-testid="btn-wtr-red-dates">
            <Settings2 className="w-4 h-4 mr-1" /> Atur Tanggal Merah
          </Button>
        </div>
      </div>

      <Card className="rounded-sm border-slate-200 bg-white p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <div className="label-ind">Pilih Bulan</div>
            <Select value={month || ""} onValueChange={(v) => load(v)}>
              <SelectTrigger className="h-9 mt-1 rounded-sm" data-testid="wtr-month-select">
                <SelectValue placeholder="Pilih bulan" />
              </SelectTrigger>
              <SelectContent>
                {(months || []).map((m) => (
                  <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-xs text-slate-500">
            <div className="label-ind mb-1">Bulan Aktif</div>
            <div className="font-mono">{month || "-"}</div>
          </div>
          <div className="text-xs text-slate-500">
            <div className="label-ind mb-1">Tanggal Merah</div>
            <div className="font-mono">{redDates || "-"}</div>
          </div>
        </div>
      </Card>

      <Card className="rounded-sm border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 flex items-center gap-2 text-sm">
          <CalendarDays className="w-4 h-4 text-slate-500" />
          <span className="font-semibold text-slate-800">Tabel WTR SI</span>
          {savingKey && <span className="text-xs text-slate-500 font-mono ml-auto">Menyimpan...</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-[1700px] w-full border-collapse" data-testid="wtr-table">
            <thead>
              <tr>
                <th className="sticky left-0 z-20 bg-slate-100 min-w-[220px]">Name</th>
                <th className="sticky left-[220px] z-20 bg-slate-100 min-w-[90px]">Team</th>
                {Array.from({ length: DAY_COUNT }, (_, i) => i + 1).map((day) => (
                  <th
                    key={day}
                    className={`text-center min-w-[52px] border border-slate-300 ${dayHeaderClass(month, day, redDatesSet)}`}
                  >
                    {day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={DAY_COUNT + 2} className="text-center py-8 text-slate-500">Memuat data...</td>
                </tr>
              )}
              {!loading && groupedTeams.length === 0 && (
                <tr>
                  <td colSpan={DAY_COUNT + 2} className="text-center py-8 text-slate-400">Belum ada data untuk bulan ini.</td>
                </tr>
              )}
              {!loading && groupedTeams.map((group, gi) => (
                <React.Fragment key={group.team}>
                  {group.rows.map((row, ri) => (
                    <tr key={`${row.__row}-${row.name}-${ri}`} className={row.type === "jadwal" ? "bg-slate-50/70" : ""}>
                      <td className="sticky left-0 z-10 bg-white font-semibold text-slate-800 text-xs">
						{row.name}
                        {row.type === "jadwal" && (
                          <span className="ml-2 px-1.5 py-0.5 text-[10px] rounded-sm bg-slate-200 text-slate-600 uppercase">Jadwal</span>
                        )}
                      </td>
                      <td className="sticky left-[220px] z-10 bg-white font-mono">{row.team || "-"}</td>
                      {Array.from({ length: DAY_COUNT }, (_, i) => i + 1).map((day) => {
                        const key = `${row.__row}-${day}`;
                        const isEditing = editing === key;
                        const value = row[`d${day}`] || "";
                        const bgClass = row.type === "jadwal" ? jadwalCellClass(value) : "";
                        return (
                          <td
                            key={key}
                            className={`text-center p-1 border border-slate-200 ${bgClass} ${user ? "cursor-pointer" : "cursor-not-allowed"} ${savingKey === key ? "opacity-70" : ""}`}
                            onClick={() => beginEdit(row, day)}
                          >
                            {isEditing ? (
                              <Input
                                autoFocus
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onBlur={() => finishEdit(row, day, true)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    finishEdit(row, day, true);
                                  }
                                  if (e.key === "Escape") {
                                    e.preventDefault();
                                    finishEdit(row, day, false);
                                  }
                                }}
                                className="h-8 rounded-sm text-center px-1"
                              />
                            ) : (
                              <span className="font-mono text-xs">{value}</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {gi < groupedTeams.length - 1 && (
                    <tr>
                      <td colSpan={DAY_COUNT + 2} className="h-2 bg-slate-100 border-y border-slate-200" />
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Atur Tanggal Merah</DialogTitle>
            <DialogDescription>
              Masukkan tanggal merah untuk bulan <span className="font-mono">{month || "-"}</span>, pisahkan dengan koma. Contoh: 2,16,17
            </DialogDescription>
          </DialogHeader>
          <div>
            <div className="label-ind mb-1">Tanggal Merah</div>
            <Input
              value={redDatesInput}
              onChange={(e) => setRedDatesInput(e.target.value)}
              placeholder="2,16,17"
              data-testid="wtr-red-dates-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfig(false)} className="rounded-sm">Batal</Button>
            <Button onClick={saveRedDates} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
